import { Alert, Badge, Button, Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import WhatsappMetaAPI from './WhatsappMetaAPI';

const metaAPI = new WhatsappMetaAPI();
const META_APP_ID = "1569142691194026";
const META_CONFIG_ID = "1358874219038610";
const META_GRAPH_VERSION = 'v24.0';

const isMetaOrigin = (origin) => {
  try {
    const hostname = new URL(origin).hostname;
    return hostname === 'facebook.com' || hostname.endsWith('.facebook.com');
  } catch {
    return false;
  }
};

export default function WhatsAppConnectPage() {
  const [sdkReady, setSdkReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const authorizationCodeRef = useRef(null);
  const signupDataRef = useRef(null);
  const finishingRef = useRef(false);

  const resetAttempt = useCallback(() => {
    authorizationCodeRef.current = null;
    signupDataRef.current = null;
    finishingRef.current = false;
  }, []);

  const finishConnectionWhenReady = useCallback(async () => {
    const code = authorizationCodeRef.current;
    const signupData = signupDataRef.current;

    if (!code || finishingRef.current) {
      return;
    }

    finishingRef.current = true;

    try {
      await metaAPI.finishConnection({
        code,
        wabaId: signupData?.wabaId,
        phoneNumberId: signupData?.phoneNumberId,
      });

      setSuccessMessage('WhatsApp conectado com sucesso.');
      setErrorMessage('');
      resetAttempt();
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      setErrorMessage(apiMessage || 'A Meta autorizou o acesso, mas não foi possível finalizar a conexão.');
      setSuccessMessage('');
      finishingRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [resetAttempt]);

  useEffect(() => {
    if (!META_APP_ID || !META_CONFIG_ID) {
      setErrorMessage('A integração da Meta não está configurada. Informe REACT_APP_META_APP_ID e REACT_APP_META_CONFIG_ID.');
      return undefined;
    }

    const initializeFacebookSdk = () => {
      window.FB.init({
        appId: META_APP_ID,
        cookie: true,
        xfbml: false,
        version: META_GRAPH_VERSION,
      });
      setSdkReady(true);
    };

    window.fbAsyncInit = initializeFacebookSdk;

    if (window.FB) {
      initializeFacebookSdk();
      return undefined;
    }

    let script = document.getElementById('facebook-jssdk');
    if (!script) {
      script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.onerror = () => {
        setErrorMessage('Não foi possível carregar o SDK da Meta. Verifique sua conexão e tente novamente.');
      };
      document.body.appendChild(script);
    }

    return undefined;
  }, []);

  useEffect(() => {
    const handleEmbeddedSignupMessage = (event) => {
      if (!isMetaOrigin(event.origin)) {
        return;
      }

      let payload = event.data;
      try {
        if (typeof payload === 'string') {
          payload = JSON.parse(payload);
        }
      } catch {
        return;
      }

      if (payload?.type !== 'WA_EMBEDDED_SIGNUP') {
        return;
      }

      if (payload.event === 'FINISH') {
        const wabaId = payload.data?.waba_id;
        const phoneNumberId = payload.data?.phone_number_id;

        if (!wabaId || !phoneNumberId) {
          setLoading(false);
          setErrorMessage('A Meta concluiu a autorização, mas não retornou a conta ou o número do WhatsApp. Tente novamente.');
          resetAttempt();
          return;
        }

        signupDataRef.current = {
          wabaId,
          phoneNumberId,
        };
        finishConnectionWhenReady();
        return;
      }

      if (payload.event === 'CANCEL') {
        setLoading(false);
        setErrorMessage('A conexão com o WhatsApp foi cancelada antes da conclusão.');
        resetAttempt();
        return;
      }

      if (payload.event === 'ERROR') {
        setLoading(false);
        setErrorMessage(payload.data?.error_message || 'A Meta informou um erro durante a configuração do WhatsApp.');
        resetAttempt();
      }
    };

    window.addEventListener('message', handleEmbeddedSignupMessage);
    return () => window.removeEventListener('message', handleEmbeddedSignupMessage);
  }, [finishConnectionWhenReady, resetAttempt]);

  const handleConnect = () => {
    if (!window.FB || !sdkReady) {
      setErrorMessage('O SDK da Meta ainda não está disponível. Aguarde alguns segundos e tente novamente.');
      return;
    }

    resetAttempt();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    window.FB.login(
      (response) => {
        const code = response?.authResponse?.code;

        if (!code) {
          setLoading(false);
          setErrorMessage('A autorização da Meta não foi concluída.');
          resetAttempt();
          return;
        }

        authorizationCodeRef.current = code;
        finishConnectionWhenReady();
      },
      {
        config_id: META_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3',
        },
      }
    );
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow-sm border-0">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h4 className="mb-0">Conectar WhatsApp</h4>
                <Badge bg="success">Meta</Badge>
              </div>

              <p className="text-muted mb-4">
                Conecte o WhatsApp Business do seu estabelecimento para enviar e receber mensagens pelo Hallapp.
              </p>

              {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
              {successMessage && <Alert variant="success">{successMessage}</Alert>}

              <Button
                variant="success"
                onClick={handleConnect}
                disabled={loading || !sdkReady || !META_APP_ID || !META_CONFIG_ID}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Finalizando conexão...
                  </>
                ) : (
                  'Conectar WhatsApp'
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
